/**
 * The signed-in user, read from where useAuth persists it.
 *
 * The app has no auth context: `useAuth` keeps the user in React state and
 * mirrors it to localStorage, and every screen that needs it has it threaded
 * down as a prop. That works, but it means a screen reached through a route
 * that does not thread the prop sees nothing.
 *
 * That is exactly the situation across the two UIs — the old one renders
 * `<AgentPage unrestrictedTargets={...} />` while the new one renders a bare
 * `<AgentPage />`. Rather than require both route tables to stay in step
 * forever, screens can fall back to this.
 *
 * Read-only and best-effort. It is a convenience for UI defaults, never an
 * authorisation check: the server decides what a user may actually do, and
 * every flag here is re-read from the database on each request.
 */
export function getStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private mode, cleared storage, or a corrupted entry — treat as signed
    // out rather than throwing inside a render.
    return null;
  }
}

/**
 * May this user type any URL, instead of picking from a project's domains?
 *
 * Mirrors User.unrestrictedTargets on the server. Defaults to false, so a
 * missing or unreadable value gives the normal, restricted experience.
 */
export function hasUnrestrictedTargets() {
  return !!getStoredUser()?.unrestrictedTargets;
}
